import { Injectable, NgZone } from '@angular/core';

@Injectable()
export class AnalyticsService {
  constructor(private zone: NgZone) {}

  private track(event: AnalyticsEventsEnum, data: any): void {
    const mtm = (window as any)._mtm || [];

    if (!mtm) {
      return;
    }

    this.zone.runOutsideAngular((e) =>
      mtm.push({
        event,
        ...data,
      }),
    );
  }

  private trackPage(
    event: AnalyticsEventsEnum,
    pageType: AnalyticsPageTypeEnum,
    data: any,
  ) {
    this.track(event, {
      pageType: pageType,
      ...data,
    });
  }

  public trackPageView(page: { userId?: string; pageName: string }): void {
    this.track(AnalyticsEventsEnum.PageView, {
      userId: page.userId,
      pageName: page.pageName,
    });
  }

  public trackServiceStagePageView(page: {
    userId?: string;
    requestId?: string;
    serviceName?: string;
    formName?: string;
    pageName: string;
    formStepName?: string;
  }): void {
    this.track(AnalyticsEventsEnum.PageView, {
      userId: page.userId,
      requestId: page.requestId,
      serviceName: page.serviceName,
      formName: page.formName,
      pageName: page.pageName,
      formStepName: page.formStepName,
      pageType: AnalyticsPageTypeEnum.ServiceStage,
    });
  }

  public trackVirtualPageView(virtualPage: {
    customUrl: string;
    referrerUrl: string;
    requestId: string | number;
    pageType: AnalyticsPageTypeEnum;
    serviceName: string;
    formName: string;
    pageName: string;
    formStepName: string;
  }): void {
    this.track(AnalyticsEventsEnum.VirtualPageView, virtualPage);
  }

  public trackServiceCardsPageView(properties: IAnalyticsPageViewEvent): void {
    this.trackPage(
      AnalyticsEventsEnum.VirtualPageView,
      AnalyticsPageTypeEnum.PreServiceStage,
      properties,
    );
  }

  public formError(event: {
    formName: string;
    fieldName: string;
    errors: string;
  }) {
    this.track(AnalyticsEventsEnum.FormError, {
      formName: event.formName,
      fieldName: event.fieldName,
      errorType: AnalyticsErrorType.Validation,
      errors: event.errors,
    });
  }

  public serviceStepSuccess(event: { formName: string; formStepName: string }) {
    this.track(AnalyticsEventsEnum.ServiceStepSuccess, {
      formName: event.formName,
      formStepName: event.formStepName,
      eventCategory: AnalyticsEventCategoryEnum.Forms,
      eventAction: AnalyticsEventActionEnum.ServiceStepSuccess,
    });
  }

  public formSubmit(event: { formName: string; requestId: string }) {
    this.track(AnalyticsEventsEnum.FormSubmit, {
      formName: event.formName,
      requestId: event.requestId,
      eventCategory: AnalyticsEventCategoryEnum.Forms,
      eventAction: AnalyticsEventActionEnum.FormSubmit,
    });
  }

  public formDraft(event: {
    formName: string;
    requestId: string;
    formStepName: string;
  }) {
    this.track(AnalyticsEventsEnum.FormDraft, {
      formName: event.formName,
      requestId: event.requestId,
      formStepName: event.formStepName,
      eventCategory: AnalyticsEventCategoryEnum.Forms,
      eventAction: AnalyticsEventActionEnum.SaveAsDraft,
    });
  }

  public loadExistingDraft(event: {
    formName: string;
    requestId: string;
    formStepName: string;
  }) {
    this.track(AnalyticsEventsEnum.LoadExistingDraft, {
      formName: event.formName,
      requestId: event.requestId,
      formStepName: event.formStepName,
      eventCategory: AnalyticsEventCategoryEnum.Forms,
      eventAction: AnalyticsEventActionEnum.LoadDraft,
    });
  }

  public formStepError(event: {
    formName: string;
    errors: string;
    formStepName: string;
  }) {
    this.track(AnalyticsEventsEnum.FormStepError, {
      formName: event.formName,
      errors: event.errors,
      formStepName: event.formStepName,
      eventCategory: AnalyticsEventCategoryEnum.Forms,
      eventAction: `${AnalyticsEventActionEnum.ErrorOnStep} ${event.formStepName}`,
    });
  }

  public formSubmitValidateError(event: { formName: string; errors: string }) {
    this.track(AnalyticsEventsEnum.FormSubmitError, {
      eventCategory: AnalyticsEventCategoryEnum.Forms,
      eventAction: AnalyticsEventActionEnum.ErrorOnSubmit,
      errorType: AnalyticsErrorType.Validation,
      formName: event.formName,
      errors: event.errors,
    });
  }

  public paymentSuccess(event: { formName: string; requestId: string }) {
    this.track(AnalyticsEventsEnum.PaymentSuccess, {
      formName: event.formName,
      requestId: event.requestId,
      eventCategory: AnalyticsEventCategoryEnum.Forms,
      eventAction: AnalyticsEventActionEnum.Success,
    });
  }

  public paymentError(event: {
    formName: string;
    errors: string;
    requestId?: string;
  }) {
    this.track(AnalyticsEventsEnum.PaymentError, {
      formName: event.formName,
      eventCategory: AnalyticsEventCategoryEnum.Forms,
      eventAction: AnalyticsEventActionEnum.ErrorOnPayment,
      errors: event.errors,
      errorType: AnalyticsErrorType.Payment,
      requestId: event.requestId,
    });
  }

  public paymentValidationError(event: {
    formName: string;
    requestId: string;
  }) {
    this.track(AnalyticsEventsEnum.PaymentError, {
      formName: event.formName,
      requestId: event.requestId,
      eventCategory: AnalyticsEventCategoryEnum.Forms,
      eventAction: AnalyticsEventActionEnum.ErrorOnPayment,
      errorType: AnalyticsErrorType.Validation,
    });
  }

  public technicalValidationError(event: { errors: string }) {
    this.track(AnalyticsEventsEnum.TechnicalError, {
      errorType: AnalyticsErrorType.Validation,
      errors: event.errors,
    });
  }

  public technicalTimeoutError(event: { errors: string }) {
    this.track(AnalyticsEventsEnum.TechnicalError, {
      errorType: AnalyticsErrorType.Timeout,
      errors: event.errors,
    });
  }

  public technicalPaymentError(event: { errors: string }) {
    this.track(AnalyticsEventsEnum.TechnicalError, {
      errorType: AnalyticsErrorType.Payment,
      errors: event.errors,
    });
  }

  public filterUse(event: { filter: string }) {
    this.track(AnalyticsEventsEnum.FilterUse, {
      filter: event.filter,
    });
  }

  public languageSwitch(event: { language: 'AR' | 'EN' }) {
    this.track(AnalyticsEventsEnum.LanguageSwitch, {
      language: event.language,
    });
  }

  public internalSearch(event: { searchQuery: string }) {
    this.track(AnalyticsEventsEnum.InternalSearch, {
      searchQuery: event.searchQuery,
    });
  }

  public formUploadStart(event: { formName: string; formStepName: string }) {
    this.track(AnalyticsEventsEnum.FormUploadStart, {
      eventCategory: AnalyticsEventCategoryEnum.Forms,
      eventAction: AnalyticsEventActionEnum.UploadStart,
      formName: event.formName,
      formStepName: event.formStepName,
    });
  }

  public formUploadSuccess(event: { formName: string; formStepName: string }) {
    this.track(AnalyticsEventsEnum.FormUploadSuccess, {
      eventCategory: AnalyticsEventCategoryEnum.Forms,
      eventAction: AnalyticsEventActionEnum.UploadSuccess,
      formName: event.formName,
      formStepName: event.formStepName,
    });
  }
}

interface IAnalyticsPageViewEvent {
  userId?: string;
  requestId?: string;
  formName?: string;
  pageName?: string;
}

interface IAnalyticsVirtualPageViewEvent extends IAnalyticsPageViewEvent {
  customUrl: string;
}

interface IAnalyticsServiceEventProperties extends IAnalyticsVirtualPageViewEvent {
  serviceName: string;
  stepNumber: string;
}

export enum AnalyticsPageTypeEnum {
  PreServiceStage = 'Pre-service stage',
  ServiceStage = 'Service stage',
  CompletionStage = 'Completion stage',
}

enum AnalyticsEventsEnum {
  PageView = 'pageView',
  VirtualPageView = 'virtualPageView',
  FormError = 'formError',
  ServiceStepSuccess = 'serviceStepSuccess',
  FormSubmit = 'formSubmit',
  FormDraft = 'formDraft',
  LoadExistingDraft = 'loadExistingDraft',
  FormStepError = 'formStepError',
  FormSubmitError = 'formSubmitError',
  PaymentSuccess = 'paymentSuccess',
  PaymentError = 'paymentError',
  TechnicalError = 'technicalError',
  FilterUse = 'filterUse',
  LanguageSwitch = 'languageSwitch',
  InternalSearch = 'internalSearch',
  FormUploadStart = 'formUploadStart',
  FormUploadSuccess = 'formUploadSuccess',
}

enum AnalyticsEventCategoryEnum {
  Forms = 'Forms',
}

enum AnalyticsEventActionEnum {
  Success = 'event success',
  FormSubmit = 'form submit',
  SaveAsDraft = 'save as draft',
  LoadDraft = 'Load an existing draft',
  ErrorOnStep = 'Error on step',
  ErrorOnPayment = 'Error on payment',
  UploadStart = 'upload start',
  UploadSuccess = 'upload success',
  ErrorOnSubmit = 'error on submit',
  ServiceStepSuccess = 'service step success',
}

enum AnalyticsErrorType {
  Validation = 'validation',
  Payment = 'payment',
  Timeout = 'timeout',
}
